import React from 'react';
import {List, useRedirect, useTranslate} from 'react-admin';
import {Box, Typography} from '@mui/material';
import {AddRounded} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import StatamicListPage from '../components/statamic/StatamicListPage';
import PrimaryButton from '../components/statamic/PrimaryButton';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import MonoChip from '../components/statamic/MonoChip';

const TextCell = ({value}) => (
    <Typography
        sx={{
            fontSize: `${t.type.body.size}px`,
            color: value ? t.color.text.secondary : t.color.text.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }}
    >
        {value ?? '—'}
    </Typography>
);

const IdentifierCell = ({record}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <StatusDot variant={record.active ? 'success' : 'neutral'}/>
        <Typography
            sx={{
                fontSize: `${t.type.bodyStrong.size}px`,
                fontWeight: t.type.bodyStrong.weight,
                color: t.color.text.primary,
                fontFamily: t.font.mono,
                whiteSpace: 'nowrap',
            }}
        >
            {record.action_identifier || '—'}
        </Typography>
    </Box>
);

const RNActionListStatamicInner = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    const columns = [
        {
            key: 'identifier', label: 'Action identifier',
            render: (r) => <IdentifierCell record={r}/>,
        },
        {
            key: 'action_type', label: 'Action type', width: 120,
            render: (r) => r.action_type
                ? <MonoChip>{r.action_type}</MonoChip>
                : <TextCell value={null}/>,
        },
        {
            key: 'device_type', label: 'Device type', width: 120,
            render: (r) => r.device_type
                ? <MonoChip>{r.device_type}</MonoChip>
                : <TextCell value={null}/>,
        },
        {
            key: 'url', label: 'URL',
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.caption.size}px`, fontFamily: t.font.mono, color: t.color.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {r.url ?? '—'}
                </Typography>
            ),
        },
        {
            key: 'active', label: translate('Is active'), width: 110,
            render: (r) => (
                <StatusPill variant={r.active ? 'success' : 'neutral'}>
                    {r.active ? translate('Active') : translate('Inactive')}
                </StatusPill>
            ),
        },
    ];

    const rowActions = (record) => [
        {label: translate('Edit'), onClick: () => redirect('edit', 'rnActions', record.id)},
    ];

    return (
        <StatamicListPage
            title="RN Actions"
            subtitle="Deep links and webview entry points for the mobile app"
            headerActions={
                <PrimaryButton
                    variant="primary"
                    startIcon={<AddRounded sx={{fontSize: 18}}/>}
                    onClick={() => redirect('create', 'rnActions')}
                >
                    Create RN action
                </PrimaryButton>
            }
            columns={columns}
            rowActions={rowActions}
            onRowClick={(record) => redirect('edit', 'rnActions', record.id)}
        />
    );
};

const RNActionListStatamic = (props) => (
    <List
        {...props}
        perPage={25}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <RNActionListStatamicInner/>
    </List>
);

export default RNActionListStatamic;
